import React from 'react';
import { makeStyles, createStyles } from '@material-ui/core';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Box from '@material-ui/core/Box';
import FormHelperText from '@material-ui/core/FormHelperText';
import { FormState } from '../../hooks/useFormState.types';
import isNil from 'lodash/isNil';

const useStyles = makeStyles(() =>
  createStyles({
    radioHelper: {
      marginTop: -8,
      marginLeft: 54,
    },
    radioControl: {
      flexGrow: 1,
      flexShrink: 0,
      flexBasis: 0,
      '& .MuiFormControlLabel-root': {
        width: '100%',
        '& .MuiFormControlLabel-label': {
          display: 'inline-block',
          width: '100%',
        },
      },
    },
  }),
);

function getNextInput(element: HTMLElement | null): HTMLInputElement | null {
  if (!element) {
    return element;
  }
  if (element instanceof HTMLInputElement) {
    return element;
  }
  return element.querySelector('input');
}

function focus(element: HTMLInputElement | null) {
  if (element !== null) {
    element.focus();
  }
}

type Props<Model> = {
  fieldName: keyof Model;
  helper?: React.ReactNode;
  selectorField: keyof Model;
  form: FormState<Model>;
  inputRef: React.MutableRefObject<any>;
  field: React.ReactElement;
  radioTestId?: string;
};

export default function SelectableInput<Model extends Record<string, any>>({
  fieldName,
  selectorField,
  form,
  inputRef,
  field,

  helper,
  radioTestId,
}: Props<Model>) {
  const classes = useStyles();
  const selectorValue = form.values[selectorField];
  const error = form.errors[fieldName];

  return (
    <Box className={classes.radioControl} key={fieldName as string}>
      <FormControlLabel
        value={fieldName}
        control={
          <Radio
            onChange={async () => {
              if (selectorValue !== fieldName) {
                await form.setValue(selectorField, fieldName);
              }
            }}
            checked={selectorValue === fieldName}
            inputProps={{
              type: 'checkbox',
            }}
            data-testid={radioTestId}
            tabIndex={selectorValue === fieldName ? -1 : 0}
            color={'primary'}
            onClick={() => focus(getNextInput(inputRef.current))}
            onKeyDown={async (e: React.KeyboardEvent) => {
              if (e.key === 'Enter') {
                await form.setValue(selectorField, fieldName);
                focus(getNextInput(inputRef.current));
              }
            }}
          />
        }
        label={field}
      />
      {!isNil(error) && selectorValue === fieldName ? (
        <FormHelperText className={classes.radioHelper} error>
          <span>{error}</span>
        </FormHelperText>
      ) : null}
      {helper}
    </Box>
  );
}
